import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { validateImg, ValidatePdf } from '../../../utils'

const useFileUpload = ({
    name,
    recordID = '',
    accept = 'image/*',
    multiple = false,
    maxFiles = 5,
    maxFileSize = 10 * 1024 * 1024,
    onUpload,
    onClose
}) => {

    const { t } = useTranslation()
    const fileInputRef = useRef(null)
    const [files, setFiles] = useState([])
    const [previews, setPreviews] = useState([])
    const [isDragging, setIsDragging] = useState(false)
    const [errors, setErrors] = useState({})

    const modalName = useMemo(() => `${name}${recordID || ''}`, [name, recordID])

    const validateFile = useCallback((file) => {
        const errors = []

        if (file.size > maxFileSize) errors.push(t('file-size-exceeded', { size: `${(maxFileSize / 1024 / 1024).toFixed(0)}MB` }))

        if (accept === 'image/*' && !validateImg(file)) {
            errors.push(t('Please enter a valide img'))
        } else if (accept === 'application/pdf' && !ValidatePdf(file)) {
            errors.push(t('invalid-file-type'))
        } else if (
            accept &&
            accept !== '*' &&
            accept !== 'image/*' &&
            accept !== 'application/pdf'
        ) {
            const acceptedTypes = accept.split(',').map((type) => type.trim())
            const fileType = file.type
            if (!acceptedTypes.some((type) => fileType.includes(type.replace('*', '')))) {
                errors.push(t('invalid-file-type'))
            }
        }
        return errors
    }, [accept, maxFileSize])

    const processFiles = useCallback((fileList) => {
        const fileArray = Array.from(fileList)
        const newFiles = []
        const newPreviews = []
        const newErrors = {}

        fileArray.forEach((file, index) => {
            const fileId = `${Date.now()}-${index}-${file.name}`
            const validationErrors = validateFile(file)

            if (validationErrors.length > 0) {
                newErrors[fileId] = validationErrors
            }

            newFiles.push({ id: fileId, value: file })

            if (validateImg(file)) {
                const reader = new FileReader()
                reader.onloadend = () => {
                    setPreviews((prev) => {
                        const existing = prev.filter((p) => p.id !== fileId)
                        return [...existing, { id: fileId, preview: reader.result }]
                    })
                }
                reader.readAsDataURL(file)
            } else {
                newPreviews.push({ id: fileId, preview: null, fileType: file.type })
            }
        })

        setFiles((prev) => {
            const combined = multiple ? [...prev, ...newFiles] : [...newFiles]
            return combined.slice(0, multiple ? maxFiles : 1)
        })

        if (newPreviews.length > 0) {
            setPreviews((prev) => {
                if (multiple) {
                    const combined = [...prev, ...newPreviews]
                    return combined.slice(0, maxFiles)
                }
                return newPreviews.slice(0, 1)
            })
        }
        setErrors((prev) => ({ ...prev, ...newErrors }))

    }, [multiple, maxFiles, validateFile])

    const handleFileSelect = useCallback((e) => {
        if (e.target.files && e.target.files.length > 0)
            processFiles(e.target.files)

    }, [processFiles])

    const handleDragOver = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
    }, [])

    const handleDragAction = useCallback((e, isDrage) => {
        handleDragOver(e)
        setIsDragging(isDrage)
    }, [handleDragOver])

    const handleDragEnter = useCallback((e) => handleDragAction(e, true), [handleDragOver])

    const handleDragLeave = useCallback((e) => handleDragAction(e, false), [handleDragOver])

    const handleDrop = useCallback((e) => {

        handleDragAction(e, false)

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) processFiles(e.dataTransfer.files)

    }, [processFiles, handleDragOver])

    const handleRemoveFile = useCallback((fileId) => {
        setFiles((prev) => prev.filter((f) => f.id !== fileId))
        setPreviews((prev) => prev.filter((p) => p.id !== fileId))
        setErrors((prev) => {
            const newErrors = { ...prev }
            delete newErrors[fileId]
            return newErrors
        })
    }, [])

    const handleClose = useCallback(() => {
        setFiles([])
        setPreviews([])
        setErrors({})
        if (fileInputRef.current) fileInputRef.current.value = ''

    }, [])

    const handleUpload = useCallback(() => {
        const hasErrors = Object.keys(errors).length > 0 || files.length === 0

        if (hasErrors) return

        const filesToUpload = !!multiple ? files.map((f) => f.value) : files[0]?.value

        onUpload?.(filesToUpload, handleClose)

    }, [files, errors, multiple, onUpload])


    useEffect(() => {
        const modal = document.getElementById(modalName);

        if (modal) return

        const handleHidden = () => {
            handleClose()
            setIsDragging(false)
            onClose?.()
            modal.addEventListener('hidden.bs.modal', handleHidden)
            return () => modal.removeEventListener('hidden.bs.modal', handleHidden)
        }
    }, [modalName, onClose, handleClose])

    const getFileIcon = useCallback((fileType) => {
        if (fileType?.includes('pdf')) return 'mdi:file-pdf-box'

        if (fileType?.includes('word')) return 'mdi:file-word-box'

        if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return 'mdi:file-excel-box'

        return 'mdi:file-document-outline'
    }, [])

    const formatFileSize = useCallback((bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }, [])

    return {
        fileInputRef,
        files,
        previews,
        isDragging,
        errors,
        modalName,
        handleFileSelect,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleRemoveFile,
        handleUpload,
        handleClose,
        getFileIcon,
        formatFileSize
    }
}

export default useFileUpload

